/**
 * ============================================================================
 * USER MODAL: View Phone Numbers & Campaign Anti-Tamper Contact Links
 * ============================================================================
 * Purpose:
 *   Displays full list of phone contacts, generated tokens, individual click counters,
 *   last clicked timestamps, and direct Excel (.xlsx) export.
 *
 * Visual Alignment:
 *   Faithfully matches Screenshot 2 (Modal layout & filters) and Screenshot 3 (Excel format).
 * ============================================================================
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import {
  XMarkIcon,
  PhoneIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

import tinyUrlService from "../../services/tinyUrlService";

export default function ViewPhoneNumbersModal({
  isOpen,
  onClose,
  campaignId,
  campaignData,
}) {
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // 'all' | 'clicked' | 'unopened'
  const [copiedToken, setCopiedToken] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Load Campaign Details
  const fetchCampaignDetails = useCallback(async () => {
    if (!campaignId && !campaignData?._id && !campaignData?.code) return;
    const targetId = campaignId || campaignData?._id || campaignData?.code;

    setLoading(true);
    try {
      const res = await tinyUrlService.getCampaignById(targetId);
      const payload = res?.data || res;
      if (payload && (payload.campaign || payload.contacts)) {
        setCampaign(payload.campaign || campaignData);
        setContacts(payload.contacts || []);
        setAllContacts(payload.allContacts || payload.contacts || []);
      } else if (campaignData) {
        setCampaign(campaignData);
        setContacts(campaignData.contacts || []);
        setAllContacts(campaignData.contacts || []);
      }
    } catch (err) {
      console.error("Failed to load campaign contacts:", err);
      // Fallback to passed campaignData if available
      if (campaignData) {
        setCampaign(campaignData);
        setContacts(campaignData.contacts || []);
        setAllContacts(campaignData.contacts || []);
      }
    } finally {
      setLoading(false);
    }
  }, [campaignId, campaignData]);

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setFilter("all");
      setCurrentPage(1);
      fetchCampaignDetails();
    }
  }, [isOpen, fetchCampaignDetails]);

  // Keyboard Escape listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Effective campaign info
  const effectiveCampaign = campaign || campaignData || {};
  const baseUrl =
    effectiveCampaign.tinyBaseUrl ||
    window.location.origin;

  // Filtered Contacts
  const filteredContacts = useMemo(() => {
    let list = allContacts.length > 0 ? allContacts : contacts;

    // Filter by click status
    if (filter === "clicked") {
      list = list.filter((c) => (c.clicks || 0) > 0);
    } else if (filter === "unopened") {
      list = list.filter((c) => !c.clicks || c.clicks === 0);
    }

    // Search query
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => {
        const phone = String(c.phoneNumber || "").toLowerCase();
        const tok = String(c.token || "").toLowerCase();
        const code = String(c.code || "").toLowerCase();
        const name = String(c.name || "").toLowerCase();
        return (
          phone.includes(q) ||
          tok.includes(q) ||
          code.includes(q) ||
          name.includes(q)
        );
      });
    }

    return list;
  }, [allContacts, contacts, filter, search]);

  // Paginated Slice
  const totalItems = filteredContacts.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredContacts.slice(start, start + pageSize);
  }, [filteredContacts, currentPage, pageSize]);

  // Copy helper
  const handleCopy = (text, type, id) => {
    navigator.clipboard.writeText(text);
    if (type === "token") {
      setCopiedToken(id);
      setTimeout(() => setCopiedToken(null), 2000);
    } else {
      setCopiedUrl(id);
      setTimeout(() => setCopiedUrl(null), 2000);
    }
  };

  // ─── Instant Excel Export (Matching Screenshot 3) ───
  const handleDownloadExcel = () => {
    const exportList = allContacts.length > 0 ? allContacts : contacts;
    if (exportList.length === 0) return;

    // Build data rows matching Screenshot 3 columns exactly:
    // Phone Number | Base URL | Code | Full Short URL | Token | Clicks | Last Clicked
    const excelRows = exportList.map((contact) => {
      const codeStr = contact.code || `${effectiveCampaign.code}/${contact.token}`;
      const shortUrl =
        contact.fullShortUrl || `${baseUrl}/${effectiveCampaign.code}/${contact.token}`;
      const clicks = contact.clicks || 0;
      const lastClickedStr = contact.lastClicked
        ? new Date(contact.lastClicked).toLocaleString()
        : "Never";

      return {
        "Phone Number": contact.phoneNumber || "",
        "Base URL": baseUrl,
        "Code": codeStr,
        "Full Short URL": shortUrl,
        "Token": contact.token || "",
        "Clicks": clicks,
        "Last Clicked": lastClickedStr,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelRows);

    // Set column widths for readability
    worksheet["!cols"] = [
      { wch: 18 }, // Phone Number
      { wch: 25 }, // Base URL
      { wch: 20 }, // Code
      { wch: 38 }, // Full Short URL
      { wch: 14 }, // Token
      { wch: 10 }, // Clicks
      { wch: 24 }, // Last Clicked
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");

    const safeTitle = (effectiveCampaign.title || "campaign")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .toLowerCase();
    XLSX.writeFile(workbook, `${safeTitle}_contacts_${effectiveCampaign.code || "export"}.xlsx`);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-5 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-850 w-full max-w-5xl rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-700/80 overflow-hidden flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4 bg-white dark:bg-gray-850">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-xs shrink-0">
              <PhoneIcon className="w-6 h-6 stroke-[2]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                  {effectiveCampaign.title || "Bulk Phone Campaign"}
                </h2>
                {effectiveCampaign.code && (
                  <span className="font-mono text-xs font-bold px-2.5 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/60">
                    {effectiveCampaign.code}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-400 mt-0.5">
                Detailed phone numbers, secure tokens, and individual click tracking
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            {/* Green Excel Download Button Matching Screenshot 2 & 3 */}
            <button
              onClick={handleDownloadExcel}
              disabled={filteredContacts.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#00875a] hover:bg-[#00744e] text-white text-xs font-bold shadow-md shadow-[#00875a]/25 cursor-pointer transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download Contact Links Spreadsheet (.xlsx)"
            >
              <ArrowDownTrayIcon className="w-4 h-4 stroke-[2.5]" />
              Download Excel
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center cursor-pointer transition-colors"
              title="Close modal"
            >
              <XMarkIcon className="w-5 h-5 stroke-[2]" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 bg-gray-50/40 dark:bg-gray-900/40">
          {/* 4 Stat Cards in a row (Matching Screenshot 2) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
            {/* Total Contacts */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-xs">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                Total Contacts
              </span>
              <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                {effectiveCampaign.totalContacts || allContacts.length}
              </div>
            </div>

            {/* Total Clicks */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-xs">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                Total Clicks
              </span>
              <div className="mt-2 text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {effectiveCampaign.totalClicks || 0}
              </div>
            </div>

            {/* Base URL */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-xs">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                Base URL
              </span>
              <div
                className="mt-2 text-sm font-semibold text-gray-800 dark:text-gray-200 truncate"
                title={baseUrl}
              >
                {baseUrl}
              </div>
            </div>

            {/* Status */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200/70 dark:border-gray-700/70 shadow-xs">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                Status
              </span>
              <div className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Active
              </div>
            </div>
          </div>

          {/* Search Bar & Filter Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search phone number or token..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 text-xs rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all shadow-xs"
              />
            </div>

            {/* Filter Pills */}
            <div className="inline-flex items-center p-1 rounded-xl bg-gray-200/70 dark:bg-gray-800 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setFilter("all");
                  setCurrentPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                  filter === "all"
                    ? "bg-[#3a57e8] text-white shadow-xs"
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900"
                }`}
              >
                All Contacts
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilter("clicked");
                  setCurrentPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                  filter === "clicked"
                    ? "bg-[#3a57e8] text-white shadow-xs"
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900"
                }`}
              >
                Clicked Only
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilter("unopened");
                  setCurrentPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                  filter === "unopened"
                    ? "bg-[#3a57e8] text-white shadow-xs"
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900"
                }`}
              >
                Unopened
              </button>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/60 dark:bg-gray-750/50 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="py-3 px-4">PHONE NUMBER</th>
                    <th className="py-3 px-4">BASE URL</th>
                    <th className="py-3 px-4">CODE (ZPN82C/TOKEN)</th>
                    <th className="py-3 px-4">FULL SHORT LINK</th>
                    <th className="py-3 px-4 text-center">CLICKS</th>
                    <th className="py-3 px-4">LAST CLICKED</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-750">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        <ArrowPathIcon className="w-6 h-6 mx-auto animate-spin text-indigo-500 mb-2" />
                        Loading contacts...
                      </td>
                    </tr>
                  ) : paginatedContacts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        <PhoneIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        No contacts found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    paginatedContacts.map((contact, index) => {
                      const codeStr =
                        contact.code || `${effectiveCampaign.code}/${contact.token}`;
                      const shortUrl =
                        contact.fullShortUrl || `${baseUrl}/${effectiveCampaign.code}/${contact.token}`;
                      const clicks = contact.clicks || 0;
                      const isCopiedTok = copiedToken === (contact._id || contact.token);
                      const isCopiedLink = copiedUrl === (contact._id || contact.token);

                      return (
                        <tr
                          key={contact._id || contact.token || index}
                          className="hover:bg-blue-50/20 dark:hover:bg-gray-750/40 transition-colors"
                        >
                          {/* PHONE NUMBER */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <PhoneIcon className="w-4 h-4 text-indigo-500 stroke-[2] shrink-0" />
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {contact.phoneNumber}
                              </span>
                              {contact.name && (
                                <span className="text-[11px] text-gray-400">
                                  ({contact.name})
                                </span>
                              )}
                            </div>
                          </td>

                          {/* BASE URL */}
                          <td className="py-3 px-4 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {baseUrl}
                          </td>

                          {/* CODE (ZPN82C/TOKEN) */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-600">
                                {codeStr}
                              </span>
                              <button
                                onClick={() =>
                                  handleCopy(codeStr, "token", contact._id || contact.token)
                                }
                                title="Copy short code"
                                className="p-1 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                              >
                                {isCopiedTok ? (
                                  <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>

                          {/* FULL SHORT LINK */}
                          <td className="py-3 px-4 max-w-xs sm:max-w-md">
                            <div className="flex items-center gap-1.5">
                              <a
                                href={shortUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 dark:text-indigo-400 hover:underline truncate max-w-xs font-mono text-[11px]"
                                title={shortUrl}
                              >
                                {shortUrl}
                              </a>
                              <button
                                onClick={() =>
                                  handleCopy(shortUrl, "url", contact._id || contact.token)
                                }
                                title="Copy full short URL"
                                className="p-1 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors shrink-0"
                              >
                                {isCopiedLink ? (
                                  <ClipboardDocumentCheckIcon className="w-3.5 h-3.5 text-emerald-500" />
                                ) : (
                                  <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <a
                                href={shortUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open link in new tab"
                                className="p-1 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors shrink-0"
                              >
                                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          </td>

                          {/* CLICKS */}
                          <td className="py-3 px-4 text-center whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/60">
                              <SparklesIcon className="w-3.5 h-3.5 text-emerald-600" />
                              {clicks}
                            </span>
                          </td>

                          {/* LAST CLICKED */}
                          <td className="py-3 px-4 whitespace-nowrap text-gray-600 dark:text-gray-300">
                            {contact.lastClicked ? (
                              new Date(contact.lastClicked).toLocaleString("en-US", {
                                month: "numeric",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true,
                              })
                            ) : (
                              <span className="text-gray-400 italic">Not opened yet</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal Footer / Pagination */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-gray-850">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {totalItems}
            </span>{" "}
            contacts
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-gray-600 dark:text-gray-400 font-semibold px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
